import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Server } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  forgotPassword,
  isAllowedRegisterEmail,
  login,
  passwordOk,
  register,
  resendVerification,
} from "../lib/authApi";

type Panel = "login" | "register" | "forgot";

function Msg({ text, kind }: { text: string; kind?: "ok" | "err" | "info" }) {
  if (!text) return null;
  const color =
    kind === "ok" ? "text-status-ok" : kind === "err" ? "text-status-error" : "text-accent";
  return (
    <p className={`mt-3 text-sm leading-snug ${color}`} role="status">
      {text}
    </p>
  );
}

export function AuthPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [panel, setPanel] = useState<Panel>("login");
  const [msg, setMsg] = useState<{ text: string; kind?: "ok" | "err" | "info" }>({ text: "" });
  const [busy, setBusy] = useState(false);

  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");

  const [regEmail, setRegEmail] = useState("");
  const [regPass, setRegPass] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState<string | null>(null);

  const regReady =
    isAllowedRegisterEmail(regEmail) && passwordOk(regPass);

  async function onLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg({ text: "" });
    const { res, data } = await login(loginUser.trim(), loginPass);
    setBusy(false);
    if (res.ok) {
      setUser(data);
      navigate("/", { replace: true });
      return;
    }
    if (data.error === "email_not_verified") {
      const email = loginUser.trim().toLowerCase();
      setPendingVerifyEmail(email.includes("@") ? email : null);
      setMsg({
        text:
          data.message ||
          "Your account is not activated yet. Use the link in your email, or resend verification below.",
        kind: "info",
      });
      return;
    }
    setPendingVerifyEmail(null);
    setMsg({ text: "Invalid email/username or password.", kind: "err" });
  }

  async function onRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!regReady) return;
    setBusy(true);
    setMsg({ text: "" });
    const email = regEmail.trim().toLowerCase();
    const { res, data } = await register(email, regPass);
    setBusy(false);
    if (data.error === "mail_not_configured" || data.error === "mail_send_failed") {
      setMsg({
        text: "We could not send email right now. Try again later or contact the administrator.",
        kind: "err",
      });
      return;
    }
    setMsg({
      text:
        data.message ||
        "If this address is eligible, we sent a verification email. Check spam — from fcr.dash.net@gmail.com.",
      kind: res.ok ? "ok" : "err",
    });
  }

  async function onForgot(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg({ text: "" });
    const email = forgotEmail.trim().toLowerCase();
    if (!email.includes("@")) {
      setMsg({ text: "Contact your administrator to reset a username-only account.", kind: "info" });
      setBusy(false);
      return;
    }
    const { data } = await forgotPassword(email);
    setBusy(false);
    setMsg({
      text:
        data.message ||
        "If an account exists, we sent a reset email. Check spam — from fcr.dash.net@gmail.com.",
      kind: "ok",
    });
  }

  return (
    <div className="auth-bg flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-[420px] rounded-xl bg-white shadow-[0_8px_40px_rgba(0,0,0,0.08)]">
        <div className="border-b border-border-subtle px-8 pb-6 pt-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-[#f0f2f5] text-[#4a4a4a]">
            <Server size={28} strokeWidth={1.5} />
          </div>
          <h1 className="text-lg font-semibold tracking-tight text-[#3d3d3d]">Blue Current</h1>
          <p className="mt-1 text-sm font-medium text-[#6b6b6b]">making impact</p>
        </div>

        {panel !== "forgot" && (
          <div className="flex border-b border-border-subtle">
            {(["login", "register"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  setPanel(tab);
                  setMsg({ text: "" });
                }}
                className={[
                  "flex-1 py-3 text-sm font-medium transition-colors",
                  panel === tab
                    ? "border-b-2 border-accent text-text-primary"
                    : "text-text-secondary hover:text-text-primary",
                ].join(" ")}
              >
                {tab === "login" ? "Sign in" : "Register"}
              </button>
            ))}
          </div>
        )}

        <div className="px-8 py-6">
          {panel === "login" && (
            <form onSubmit={onLogin}>
              <label className="mb-1 block text-xs text-text-secondary" htmlFor="login-user">
                Email or username
              </label>
              <input
                id="login-user"
                className="auth-input mb-4"
                autoComplete="username"
                required
                value={loginUser}
                onChange={(e) => setLoginUser(e.target.value)}
              />
              <label className="mb-1 block text-xs text-text-secondary" htmlFor="login-pass">
                Password
              </label>
              <input
                id="login-pass"
                type="password"
                className="auth-input mb-4"
                autoComplete="current-password"
                required
                value={loginPass}
                onChange={(e) => setLoginPass(e.target.value)}
              />
              <button type="submit" className="auth-primary" disabled={busy}>
                Sign in
              </button>
              <button
                type="button"
                className="mt-3 text-sm text-accent hover:underline"
                onClick={() => {
                  setPanel("forgot");
                  setMsg({ text: "" });
                }}
              >
                Forgot password?
              </button>
              {pendingVerifyEmail && (
                <button
                  type="button"
                  className="mt-3 text-sm text-accent hover:underline"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    const { res, data } = await resendVerification(pendingVerifyEmail);
                    setBusy(false);
                    setMsg({
                      text:
                        data.message ||
                        (res.ok
                          ? "If this address is eligible, we sent a new verification email."
                          : "Could not resend verification. Try again later."),
                      kind: res.ok ? "ok" : "err",
                    });
                  }}
                >
                  Resend verification email
                </button>
              )}
              <Msg {...msg} />
            </form>
          )}

          {panel === "register" && (
            <form onSubmit={onRegister}>
              <label className="mb-1 block text-xs text-text-secondary" htmlFor="reg-email">
                Work email
              </label>
              <input
                id="reg-email"
                type="email"
                className="auth-input mb-1"
                placeholder="you@bluecurrent.nl"
                autoComplete="email"
                required
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
              />
              <p className="mb-4 text-xs text-text-tertiary">
                Register with the work email address your organization gave you.
              </p>
              <label className="mb-1 block text-xs text-text-secondary" htmlFor="reg-pass">
                Password
              </label>
              <input
                id="reg-pass"
                type="password"
                className="auth-input mb-2"
                autoComplete="new-password"
                required
                value={regPass}
                onChange={(e) => setRegPass(e.target.value)}
              />
              <ul className="mb-4 space-y-0.5 text-xs text-text-tertiary">
                <li className={regPass.length >= 10 ? "text-status-ok" : ""}>At least 10 characters</li>
                <li className={/[A-Z]/.test(regPass) ? "text-status-ok" : ""}>At least one capital letter</li>
                <li className={/[0-9]/.test(regPass) ? "text-status-ok" : ""}>At least one number</li>
              </ul>
              <button type="submit" className="auth-primary" disabled={busy || !regReady}>
                Register
              </button>
              <Msg {...msg} />
            </form>
          )}

          {panel === "forgot" && (
            <form onSubmit={onForgot}>
              <label className="mb-1 block text-xs text-text-secondary" htmlFor="forgot-email">
                Company email
              </label>
              <input
                id="forgot-email"
                type="email"
                className="auth-input mb-4"
                required
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
              <button type="submit" className="auth-primary" disabled={busy}>
                Send reset link
              </button>
              <button
                type="button"
                className="mt-3 text-sm text-accent hover:underline"
                onClick={() => {
                  setPanel("login");
                  setMsg({ text: "" });
                }}
              >
                Back to sign in
              </button>
              <Msg {...msg} />
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
